/**
 * accountsDemo.js — client-side fixture data for the redesigned Accounts
 * page. Phase 4A (UI-only). Real Supabase wiring lands in Phase 4B.
 *
 * Guardrails:
 *   • No backend/DB touch. All shapes match the future contract exactly
 *     so swapping demo → real data is a one-liner at the hook level.
 *   • Categories: 'cash' | 'investments' | 'liabilities'.
 *   • Amounts are stored as signed base-currency numbers (negative for
 *     liabilities), same convention as the existing helpers.
 */

export const DEMO_ACCOUNTS = [
  // Cash
  { id: 'a1', name: 'HDFC Savings',   institution: 'HDFC Bank',           type: 'savings',  category: 'cash', balance: 284500, last_updated: '2026-07-10', account_number: 'XXXX 4521' },
  { id: 'a2', name: 'ICICI Current',  institution: 'ICICI Bank',          type: 'checking', category: 'cash', balance: 115200, last_updated: '2026-07-10', account_number: 'XXXX 8834' },
  { id: 'a3', name: 'SBI Savings',    institution: 'State Bank of India', type: 'savings',  category: 'cash', balance:  45000, last_updated: '2026-07-08', account_number: 'XXXX 2201' },
  { id: 'a4', name: 'Paytm Wallet',   institution: 'Paytm',               type: 'wallet',   category: 'cash', balance:   3200, last_updated: '2026-07-09' },

  // Investments
  {
    id: 'a5', name: 'Zerodha Demat', institution: 'Zerodha', type: 'brokerage', category: 'investments',
    balance: 842000, last_updated: '2026-07-10', day_change: 4820,
    holdings: [
      { name: 'Reliance Industries', ticker: 'RELIANCE', units: 15, price: 3100, value: 46500, return_pct: 12.3, type: 'stock' },
      { name: 'Infosys',             ticker: 'INFY',     units: 25, price: 1820, value: 45500, return_pct:  8.7, type: 'stock' },
      { name: 'TCS',                 ticker: 'TCS',      units: 10, price: 4120, value: 41200, return_pct: 15.2, type: 'stock' },
      { name: 'HDFC Bank',           ticker: 'HDFCBANK', units: 30, price: 1640, value: 49200, return_pct:  6.4, type: 'stock' },
      { name: 'Wipro',               ticker: 'WIPRO',    units: 50, price:  480, value: 24000, return_pct: -2.1, type: 'stock' },
    ],
  },
  {
    id: 'a6', name: 'Groww — Mutual Funds', institution: 'Groww', type: 'funds', category: 'investments',
    balance: 315000, last_updated: '2026-07-10', day_change: 1240,
    holdings: [
      { name: 'Parag Parikh Flexi Cap',         units: 450, price: 320, value: 144000, return_pct: 18.4, type: 'fund' },
      { name: 'HDFC Mid-Cap Opportunities',     units: 210, price: 520, value: 109200, return_pct: 11.2, type: 'fund' },
      { name: 'SBI Bluechip Fund',              units: 180, price: 340, value:  61200, return_pct:  6.8, type: 'fund' },
    ],
  },
  { id: 'a7', name: 'EPF Account', institution: 'EPFO', type: 'retirement', category: 'investments', balance: 1250000, last_updated: '2026-06-30' },
  { id: 'a8', name: 'PPF Account', institution: 'SBI',  type: 'retirement', category: 'investments', balance:  485000, last_updated: '2026-06-30' },

  // Liabilities (balance stored negative)
  { id: 'a9',  name: 'Home Loan',        institution: 'HDFC Bank',  type: 'mortgage',    category: 'liabilities', balance: -2480000, last_updated: '2026-07-05', interest_rate: 8.5, next_payment_date: '2026-08-05', next_payment_amount: 24500, minimum_payment: 24500, original_amount: 3500000, account_number: 'XXXX 1140' },
  { id: 'a10', name: 'Car Loan',         institution: 'HDFC Bank',  type: 'loan',        category: 'liabilities', balance:  -385000, last_updated: '2026-07-07', interest_rate: 9.2, next_payment_date: '2026-08-07', next_payment_amount: 17448, minimum_payment: 17448, original_amount:  650000, account_number: 'XXXX 2283' },
  { id: 'a11', name: 'HDFC Credit Card', institution: 'HDFC Bank',  type: 'credit-card', category: 'liabilities', balance:   -24500, credit_limit: 200000, last_updated: '2026-07-10', next_payment_date: '2026-07-20', next_payment_amount: 24500, minimum_payment: 3000 },
  { id: 'a12', name: 'Amazon Pay ICICI', institution: 'ICICI Bank', type: 'credit-card', category: 'liabilities', balance:    -8200, credit_limit:  75000, last_updated: '2026-07-09', next_payment_date: '2026-07-25', next_payment_amount:  8200, minimum_payment:  800 },
];

export const DEMO_NET_WORTH_TREND = [
  { month: 'Feb', value: 378000 },
  { month: 'Mar', value: 395000 },
  { month: 'Apr', value: 408000 },
  { month: 'May', value: 419000 },
  { month: 'Jun', value: 434000 },
  { month: 'Jul', value: 442200 },
];

export const DEMO_WHAT_CHANGED = [
  { label: 'Groww SIP added to Investments',      delta:  10000, tone: 'positive' },
  { label: 'HDFC Credit Card balance reduced',    delta:   8200, tone: 'positive' },
  { label: 'Home Loan principal paid down',       delta:   4200, tone: 'positive' },
  { label: 'Car Loan principal paid down',        delta:   2400, tone: 'positive' },
];

export const DEMO_ATTENTION = [
  { label: 'HDFC Credit Card payment due',  sub: 'Due 20 Jul · Full balance ₹24,500', urgency: 'high' },
  { label: 'Amazon Pay ICICI payment due',  sub: 'Due 25 Jul · Full balance ₹8,200',  urgency: 'medium' },
];

export const DEMO_RECENT_PAYMENTS = [
  { date: '2026-07-07', account: 'Car Loan — HDFC',   amount: -17448 },
  { date: '2026-07-05', account: 'Home Loan — HDFC',  amount: -24500 },
  { date: '2026-06-20', account: 'HDFC Credit Card',   amount: -32000 },
  { date: '2026-06-07', account: 'Car Loan — HDFC',   amount: -17448 },
  { date: '2026-06-05', account: 'Home Loan — HDFC',  amount: -24500 },
];

export const DEMO_CASH_RECENT = [
  { date: '2026-07-10', desc: 'Swiggy',         amount:   -842, cat: 'Food & Dining' },
  { date: '2026-07-07', desc: 'Car Loan EMI',   amount: -17448, cat: 'Loan Payment' },
  { date: '2026-07-06', desc: 'Groww SIP',      amount: -10000, cat: 'Investments' },
  { date: '2026-07-01', desc: 'Monthly Salary', amount:  85000, cat: 'Salary' },
  { date: '2026-07-01', desc: 'Netflix',        amount:   -649, cat: 'Subscriptions' },
];

/** Institution avatar colours (from Figma). */
export const INSTITUTION_COLOURS = {
  'HDFC Bank':           { bg: '#163A32', fg: '#A8C3B0' },
  'ICICI Bank':          { bg: '#3E6A80', fg: '#C5DDE8' },
  'State Bank of India': { bg: '#1D3E7A', fg: '#C5D4F0' },
  'Paytm':               { bg: '#00B9F1', fg: '#003F57' },
  'Zerodha':             { bg: '#387ED1', fg: '#C8DFFF' },
  'Groww':               { bg: '#00D09C', fg: '#004D3B' },
  'EPFO':                { bg: '#4A1942', fg: '#D8C5D8' },
  'SBI':                 { bg: '#1D3E7A', fg: '#C5D4F0' },
};

/** Display labels for account types (from Figma). */
export const ACCOUNT_TYPE_LABEL = {
  checking: 'Current', savings: 'Savings', wallet: 'Wallet',
  brokerage: 'Brokerage', retirement: 'Retirement', funds: 'Mutual Funds', 'other-investment': 'Other',
  'credit-card': 'Credit Card', loan: 'Loan', mortgage: 'Mortgage', 'other-liability': 'Other',
};

/**
 * Semantic colours used across the Accounts page. Kept here so we can
 * later swap to CSS variables without touching component code.
 */
export const ACCOUNTS_TONE = {
  liability: '#A84E38',
  positive:  '#3D7A63',
  warning:   '#B07210',
  invested:  '#A07830',
};
